import { ILoggerDetails } from '../types/types';
import { LogDebug } from '../helpers/Logging';
import { joinUrlParts } from '@emarketer/code-helpers';

/**
 * Parameters for the bot redirect middleware.
 * @param {string} anonymousUrl - The anonymous content base URL to redirect bots to.
 * @param {ILoggerDetails} logger - The logger instance for logging information.
 */
interface IBotRedirectExpressMiddlewareParams {
  anonymousUrl: string;
  logger?: ILoggerDetails;
}

/**
 * Sets no-cache headers on the response to prevent caching.
 * @param res - Express response object
 */
function setNoCacheHeaders(res: any): void {
  res.setHeader('Cache-Control', 'no-cache, no-store, must-revalidate, max-age=0');
  res.setHeader('Expires', new Date(Date.now()).toUTCString()); //  !IMPORTANT: for crawling paywalled content - apparently, Akamai gets confused with values like "-1" or "0" here and doesn't expire data properly on "Edge" servers, since "Expires" expects an actual date
  res.setHeader('Pragma', 'no-cache'); // !IMPORTANT: for crawling paywalled content
  res.setHeader('Edge-Control', 'no-cache, no-store, max-age=0, bypass-cache'); //  !IMPORTANT: for crawling paywalled content - the "Edge-Control" header takes the highest precedence for caching a resource on the Akamai Platform
}

/**
 * Middleware function to handle bot redirects in an Express application.
 *
 * This middleware is used to redirect bot requests to the public content base URL.
 * When a request has the 'bot' header set to 'true', it sets appropriate headers,
 * adds a bot cookie, and redirects to the anonymous URL with the slug parameter.
 *
 * !REQUIRES: Express application instance. This middleware function will use the express instance provided by the consuming service
 * !IMPORTANT: import `express` and `cookie-parser` in your service before using this middleware
 *
 * @param anonymousUrl - The anonymous content base URL to redirect bots to.
 * @param logger - The logger instance for logging information.
 * @returns A middleware function that checks for bot requests and redirects if necessary.
 */
export const botRedirectExpressMiddleware = ({ anonymousUrl, logger }: IBotRedirectExpressMiddlewareParams) => {
  LogDebug(logger, `botRedirectExpressMiddleware called with anonymousUrl: ${anonymousUrl}`);

  // Allow empty anonymousUrl for graceful handling
  const _cleanAnonymousUrl = (anonymousUrl || '').trim();

  return (req: any, res: any, next: any) => {
    LogDebug(logger, `botRedirectExpressMiddleware :: processing request for ${req.originalUrl}`);

    if (req.headers['bot'] === 'true') {
      LogDebug(logger, `botRedirectExpressMiddleware :: bot request detected for ${req.originalUrl}`);

      setNoCacheHeaders(res);
      res.setHeader('X-InsiderIntelligence-Bot', 'true'); // not really needed (except for testing), but good to have if we need to verify that ExpressJs picked up the 'bot' header from Akamai correctly and processed the request using "visitor=bot" logic
      res.cookie('em_isBot', 'true'); // so we can do extra client-side checks if needed

      const redirectUrl = joinUrlParts(_cleanAnonymousUrl, req.params.slug);
      LogDebug(logger, `botRedirectExpressMiddleware :: redirecting bot to ${redirectUrl}`);

      return res.redirect(307, redirectUrl);
    } else {
      LogDebug(logger, `botRedirectExpressMiddleware :: non-bot request, proceeding to next middleware`);
      next();
    }
  };
};
